import React, {
  createContext,
  createRef,
  useEffect,
  useCallback,
  useMemo,
  useState,
  useContext
} from 'react';
import PropTypes from 'prop-types';
import auth0 from 'auth0-js';
import jwtDecode from 'jwt-decode';

const AuthenticationContext = createContext();

const webAuthClient = new auth0.WebAuth({
  domain: process.env.REACT_APP_AUTH0_DOMAIN,
  clientID: process.env.REACT_APP_AUTH0_CLIENT_ID,
  responseType: 'token id_token',
  redirectUri: `${window.location.origin}/auth/callback`,
  audience: process.env.REACT_APP_AUTH0_AUDIENCE,
  scope: 'openid profile email'
});

const loginWithGoogle = () => webAuthClient.authorize({ connection: 'google-oauth2' });

const loginWithMicrosoft = () => webAuthClient.authorize({ connection: 'Supper-Azure' });

const AuthenticationProvider = ({ children = null }) => {
  const tokenRenewalTimeout = createRef();
  const [accessToken, setAccessToken] = useState(null);
  const [user, setUser] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const logoutHandler = (event) => {
    if (event.key === 'logout') {
      window.location = '/auth/login';
    }
  };

  const performCleanup = () => {
    setAccessToken(null);
    setUser(null);
    setExpiresAt(null);
    clearTimeout(tokenRenewalTimeout.current);
    window.removeEventListener('storage', logoutHandler);
  };

  const logout = useCallback(() => {
    webAuthClient.logout({
      returnTo: `${window.location.origin}/auth/login`,
      clientID: process.env.REACT_APP_AUTH0_CLIENT_ID
    });

    performCleanup();
    localStorage.setItem('logout', new Date().toISOString());
  }, [tokenRenewalTimeout]);

  const isAuthenticated = useCallback(() => {
    if (!accessToken) return false;
    if (!user) return false;
    if (!expiresAt || new Date().getTime() > expiresAt) return false;

    return true;
  }, [accessToken, user, expiresAt]);

  const setSession = useCallback(
    (authResult) => {
      setAccessToken(authResult.accessToken);
      setUser(jwtDecode(authResult.idToken));
      setExpiresAt(new Date().getTime() + authResult.expiresIn * 1000);

      tokenRenewalTimeout.current = setTimeout(checkSession, authResult.expiresIn * 1000);
    },
    [tokenRenewalTimeout]
  );

  const checkSession = () => {
    webAuthClient.checkSession({}, (err, authResult) => {
      if (!err) {
        setSession(authResult);
      }
      setIsLoading(false);
    });
  };

  const parseHash = () =>
    new Promise((resolve, reject) => {
      webAuthClient.parseHash((err, authResult) => {
        if (!err) {
          setSession(authResult);
          return resolve(authResult);
        }
        return reject(err);
      });
    });

  useEffect(() => {
    window.addEventListener('storage', logoutHandler);

    setIsLoading(true);
    checkSession();

    return () => performCleanup();
  }, []);

  const value = useMemo(
    () => ({
      user,
      accessToken,
      loginWithGoogle,
      loginWithMicrosoft,
      logout,
      parseHash,
      isAuthenticated,
      isLoading
    }),
    [logout, user, accessToken, isAuthenticated, isLoading, parseHash]
  );

  return <AuthenticationContext.Provider value={value}>{children}</AuthenticationContext.Provider>;
};

AuthenticationProvider.propTypes = {
  children: PropTypes.node
};

const useAuth = () => useContext(AuthenticationContext);

export { AuthenticationProvider, AuthenticationContext, useAuth };
